import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Clock, Layers } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { coverUrl } from "@/lib/training";
import { PageHeader, RoleBadge, StatusBadge, EmptyState } from "@/components/Badges";
import { ProgressBar, TrainingStatus } from "@/components/training/TrainingBits";

const CourseCard = ({ c, isAdmin }) => (
  <Link to={`/courses/${c.id}`} data-testid={`course-card-${c.id}`} className="group flex flex-col overflow-hidden rounded-2xl border border-lasso-line bg-white transition-[transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-lasso-ink">
    <div className="relative aspect-[16/9] overflow-hidden bg-lasso-tint">
      {coverUrl(c) && <img src={coverUrl(c)} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />}
      {isAdmin && <span className="absolute left-3 top-3"><StatusBadge status={c.status} /></span>}
    </div>
    <div className="flex flex-1 flex-col p-6">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-lasso-deep">{c.category}</p>
      <h3 className="mt-2 font-display text-base font-bold leading-snug">{c.title}</h3>
      <p className="mt-2 line-clamp-2 text-sm text-zinc-600">{c.description}</p>
      <div className="mt-4 flex flex-wrap gap-1.5">{c.applicable_roles.map((r) => <RoleBadge key={r} role={r} />)}</div>
      <div className="mt-auto pt-6">
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Layers className="h-3.5 w-3.5" />{c.lesson_count} lessons</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{c.estimated_minutes} min</span>
          {c.progress && <TrainingStatus status={c.progress.status} />}
        </div>
        {c.progress && c.progress.status !== "completed" && <ProgressBar pct={c.progress.pct} className="mt-3" />}
      </div>
    </div>
  </Link>
);

export default function Courses() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [courses, setCourses] = useState(null);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data));
  }, []);

  return (
    <div className="fade-up">
      <PageHeader
        eyebrow="Courses"
        title="Course catalog"
        description="Short, practical courses built from Lasso SOPs. Finish the lessons, pass the quiz, earn your certificate."
        actions={(isAdmin || user.is_region_admin) && (
          <Link to="/courses/new" className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="course-new-button">
            <Plus className="h-4 w-4" /> New course
          </Link>
        )}
      />
      {courses?.length === 0 && <EmptyState title="No courses yet" testId="courses-empty" />}
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">{courses?.map((c) => <CourseCard key={c.id} c={c} isAdmin={isAdmin} />)}</div>
    </div>
  );
}
